import { TextLink } from "@/shared/components";
import { cn } from "@/shared/utils";
import ReactMarkdown, { Components } from "react-markdown";
import remarkGfm from "remark-gfm";

export function Markdown({ markdown, className }: { markdown: string; className?: string }) {
    return (
        <div className={cn("flex flex-col gap-3", className)}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                {markdown}
            </ReactMarkdown>
        </div>
    );
}

function isExternalUrl(url: string): boolean {
    return /^https?:\/\//i.test(url);
}

const components: Components = {
    h1: ({ children }) => <h3 className="leading-m text-content-emphasis text-xl font-bold">{children}</h3>,
    h2: ({ children }) => <h4 className="text-l leading-m text-content-emphasis font-bold">{children}</h4>,
    h3: ({ children }) => <h5 className="text-m leading-m text-content-emphasis font-bold">{children}</h5>,
    h4: ({ children }) => <h6 className="text-m leading-m text-content-emphasis font-bold">{children}</h6>,
    p: ({ children }) => <p className="text-m text-content-default leading-m font-normal">{children}</p>,
    ul: ({ children }) => (
        <ul className="text-m text-content-default leading-m ml-5 flex list-disc flex-col gap-1 font-normal">{children}</ul>
    ),
    ol: ({ children }) => (
        <ol className="text-m text-content-default leading-m ml-5 flex list-decimal flex-col gap-1 font-normal">{children}</ol>
    ),
    li: ({ children }) => <li className="[&>ul]:mt-1 [&>ul]:ml-5">{children}</li>,
    strong: ({ children }) => <strong className="font-bold">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    code: ({ children }) => <code className="bg-elevated-2 text-s rounded px-1 py-0.5 font-mono font-normal">{children}</code>,
    pre: ({ children }) => <pre className="bg-elevated-2 text-s overflow-x-auto rounded-md p-3 font-mono">{children}</pre>,
    blockquote: ({ children }) => <blockquote className="border-default text-content-subtle border-l-4 pl-3 italic">{children}</blockquote>,
    hr: () => <hr className="border-default" />,
    a: ({ href, children }) => (
        <TextLink
            href={href ?? "#"}
            className="inline-flex underline"
            {...(href && isExternalUrl(href) ? { target: "_blank", rel: "noreferrer noopener" } : {})}
        >
            {children}
        </TextLink>
    ),
    img: ({ src, alt }) => (
        <img
            src={typeof src === "string" ? src : undefined}
            alt={alt ?? ""}
            loading="lazy"
            className="border-default max-w-full rounded-md border"
        />
    ),
    table: ({ children }) => (
        <div className="overflow-x-auto">
            <table className="text-m text-content-default w-full border-collapse text-left">{children}</table>
        </div>
    ),
    th: ({ children }) => <th className="border-default text-content-emphasis border px-2 py-1 font-bold">{children}</th>,
    td: ({ children }) => <td className="border-default border px-2 py-1">{children}</td>,
};
